import React, { useState, useEffect } from 'react';
import {
    Typography,
    FormControl,
    Select,
    MenuItem,
    Button,
    Box,
    Autocomplete,
    TextField,
    Drawer,
    IconButton,
    Divider,
    CircularProgress,
    Tooltip,
    InputAdornment

} from '@mui/material';
import { Close, FilterList, Search } from '@mui/icons-material';
import { filterList } from '../../services/Project';

const AdminProjectFilter = ({ onFilterApply }) => {
    const [searchValue, setSearchValue] = useState('');
    const [sortOption, setSortOption] = useState('All');
    const [filteredLocation, setFilteredLocation] = useState('all');
    const [filteredProjectType, setFilteredProjectType] = useState('all');
    const [filteredGenre, setFilteredGenre] = useState('all');
    const [isApplying, setIsApplying] = useState(false);

    // Filter data from API
    const [filtersLocation, setFiltersLocation] = useState([]);
    const [filtersProjectTypes, setFiltersProjectTypes] = useState([]);
    const [filtersGenres, setFiltersGenres] = useState([]);
    const [filtersSortBy, setFiltersSortBy] = useState([]);

    const [open, setOpen] = useState(false);

    const fetchFilters = async () => {
        try {
            const response = await filterList();
            console.log("Fetched project filters:", response);
            setFiltersLocation(response.data?.locations || []);
            setFiltersProjectTypes(response.data?.work_project_types || []);
            setFiltersGenres(response.data?.genres || []);
            setFiltersSortBy(response.data?.sort_by || []);
        } catch (error) {
            console.error("Error fetching project filters:", error);
        }
    };

    useEffect(() => {
        fetchFilters();
    }, []);

    const handleSortChange = (event) => {
        const option = event.target.value;
        setSortOption(option);
        console.log(`Sorting by: ${option}`);
    };

    const handleLocationChange = (event) => {
        const location = event.target.value;
        setFilteredLocation(location);
        console.log(`Filtering by location: ${location}`);
    };

    const handleProjectTypeChange = (event) => {
        const projectType = event.target.value;
        setFilteredProjectType(projectType);
        console.log(`Filtering by project type: ${projectType}`);
    };

    const handleGenreChange = (event) => {
        const genre = event.target.value;
        setFilteredGenre(genre);
        console.log(`Filtering by genre: ${genre}`);
    };


    const handleSearchChange = (event) => {
        const value = event.target.value;
        setSearchValue(value);

        // If search value is empty, automatically apply filters to get all projects
        if (value === '') {
            handleFilterProjects();
        }
    };

    const handleSearchKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSearchSubmit();
        }
    };

    const handleSearchSubmit = () => {
        console.log(`Searching for: ${searchValue}`);
        // Trigger filter apply with search
        handleFilterProjects();
    };




    const handleFilterProjects = async () => {
        setIsApplying(true);

        const selectedSortOptionId = filtersSortBy.find(option => option.value === sortOption)?.id;
        const selectedLocationId = filtersLocation.find(option => `${option.city}, ${option.country}` === filteredLocation)?.id;
        const selectedProjectTypeId = filtersProjectTypes.find(option => option.project_type_name === filteredProjectType)?.work_project_type_id;
        const selectedGenre = filteredGenre;

        const queryParams = {
            search: searchValue || '',
            location: selectedLocationId || "all",
            project_type: selectedProjectTypeId || "all",
            genre: selectedGenre || "all",
            sort_by: selectedSortOptionId
        };

        const queryString = new URLSearchParams(queryParams).toString();

        console.log("Applying filters with query string:", queryString);

        if (onFilterApply) {
            await onFilterApply(queryString);
        }

        setIsApplying(false);
        setOpen(false); 
    };

    const handleClearFilterProjects = () => {
        setFilteredLocation('all');
        setFilteredProjectType('all');
        setFilteredGenre('all');
        setSortOption('All');
        setSearchValue('');

        const queryParams = {
            search: '',
            location: "all",
            project_type: "all",
            genre: "all",
            sort_by: filtersSortBy.find(option => option.value === 'All')?.id
        };

        const queryString = new URLSearchParams(queryParams).toString();

        if (onFilterApply) {
            onFilterApply(queryString);
        }
    };

    const toggleDrawer = (newOpen) => () => {
        setOpen(newOpen);
    };

    // Generate applied filters label
    const getAppliedFiltersLabel = () => {
        const filters = [];

        if (sortOption !== 'All') filters.push(`Sort: ${sortOption}`);
        if (filteredLocation !== 'all') filters.push(`Location: ${filteredLocation}`);
        if (filteredProjectType !== 'all') filters.push(`Type: ${filteredProjectType}`);
        if (filteredGenre !== 'all') filters.push(`Genre: ${filteredGenre}`);
        console.log("filteredLocation", filteredLocation);
        return filters.length > 0 ? `Applied - ${filters.join(', ')}` : '';
    };

    // Get the count of applied filters correctly
    const getAppliedFiltersCount = () => {
        let count = 0;

        if (sortOption !== 'All') count++;
        if (filteredLocation !== 'all') count++;
        if (filteredProjectType !== 'all') count++;
        if (filteredGenre !== 'all') count++;

        return count;
    };

    const appliedFiltersCount = getAppliedFiltersCount();

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <Tooltip title={getAppliedFiltersLabel() || "No filters applied"}>
                    <Button
                        onClick={toggleDrawer(true)}
                        startIcon={<FilterList />}
                        variant={getAppliedFiltersLabel() ? "contained" : "text"}
                        color="primary"
                        disableElevation
                        size="small"
                        sx={{ textTransform: 'none', width: 'fit-content' }}
                    >
                        Filter Projects
                        {getAppliedFiltersLabel() && (
                            <Typography variant="caption" sx={{ ml: 1 }} component={'span'}>
                                ({appliedFiltersCount})
                            </Typography>
                        )}
                    </Button>
                </Tooltip>
            </Box>

            <Drawer
                anchor="right"
                open={open}
                onClose={toggleDrawer(false)}
                sx={{
                    zIndex: 1201,
                    "& .MuiDrawer-paper": {
                        width: "100%",
                        maxWidth: 280,
                        borderRadius: 0,
                    },
                }}
                ModalProps={{
                    keepMounted: true,
                }}
                variant="temporary"
                transitionDuration={300}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
                    <Typography variant="h6">Filter Projects</Typography>
                    <IconButton onClick={toggleDrawer(false)} size="small">
                        <Close />
                    </IconButton>
                </Box>
                <Divider />

                <Box padding={2} sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    height: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                }}>

                    {/* Search Bar */}
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search for projects"
                        variant="outlined"
                        sx={{ mb: 2 }}
                        value={searchValue}
                        onKeyDown={handleSearchKeyDown}
                        onChange={handleSearchChange}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <>
                                        {searchValue && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    sx={{ padding: 0 }}
                                                    onClick={() => setSearchValue('')}
                                                >
                                                    <Close />
                                                </IconButton>
                                            </InputAdornment>
                                        )}
                                        <InputAdornment position="end">
                                            <Tooltip title="Search" arrow placement="top">
                                                <IconButton
                                                    sx={{ paddingRight: 0 }}
                                                    onClick={handleSearchSubmit}
                                                >
                                                    <Search />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    </>
                                ),
                            },
                        }}
                    />
                    {/* Sort By */}
                    <FormControl variant="outlined" fullWidth margin="dense" size="small">
                        <Typography variant="body2" gutterBottom>Sort By</Typography>
                        <Select value={sortOption} onChange={handleSortChange}>
                            {filtersSortBy.map((option) => (
                                <MenuItem key={option.id} value={option.value}>
                                    {option.value}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Filter By Section */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                        <Typography variant="body2">Filter By</Typography>
                        {getAppliedFiltersCount() > 0 && (
                            <Button
                                variant="text"
                                size="small"
                                color="error"
                                sx={{ textTransform: 'none', py: 0, px: 0.5 }}
                                onClick={handleClearFilterProjects}
                            >
                                Clear
                            </Button>
                        )}
                    </Box>

                    {/* Location Filter */}
                    <FormControl fullWidth margin="dense" size="small">
                        <Typography variant="caption">Location</Typography>
                        <Autocomplete
                            disablePortal
                            id="location-filter"
                            size="small"
                            options={[{ id: 'all', city: 'All', country: '' }, ...filtersLocation]}
                            getOptionLabel={(option) =>
                                option.city === 'All' ? 'All' : `${option.city}, ${option.country}`
                            }
                            value={
                                filteredLocation === 'all'
                                    ? { id: 'all', city: 'All', country: '' }
                                    : filtersLocation.find(
                                        (option) => `${option.city}, ${option.country}` === filteredLocation
                                    ) || null
                            }
                            onChange={(event, newValue) => {
                                if (newValue) {
                                    handleLocationChange({
                                        target: {
                                            value:
                                                newValue.city === 'All'
                                                    ? 'all'
                                                    : `${newValue.city}, ${newValue.country}`,
                                        },
                                    });
                                }
                            }}
                            renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Select location..." />}
                        />
                    </FormControl>

                    {/* Project Type Filter */}
                    <FormControl variant="outlined" fullWidth margin="dense" size="small">
                        <Typography variant="caption">Project Type</Typography>
                        <Select value={filteredProjectType} onChange={handleProjectTypeChange}>
                            <MenuItem value="all">All</MenuItem>
                            {filtersProjectTypes.map((option) => (
                                <MenuItem key={option.work_project_type_id} value={option.project_type_name}>
                                    {option.project_type_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Genre Filter */}
                    <FormControl variant="outlined" fullWidth margin="dense" size="small">
                        <Typography variant="caption">Genre</Typography>
                        <Select value={filteredGenre} onChange={handleGenreChange}>
                            <MenuItem value="all">All</MenuItem>
                            {filtersGenres.map((option) => (
                                <MenuItem key={option.id} value={option.name}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Apply Filters Button */}
                    <Button
                        disableElevation
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{ textTransform: 'none', mt: 2 }}
                        onClick={(e) => {
                            e.stopPropagation(); // Prevent event bubbling
                            handleFilterProjects();
                        }}
                        disabled={isApplying}
                        startIcon={isApplying ? <CircularProgress size={20} /> : null}
                    >
                        {isApplying ? 'Applying...' : 'Apply Filters'}
                    </Button>
                </Box>
            </Drawer>
        </>
    );
};

export default AdminProjectFilter;